// src/pages/analytics/CampaignAnalytics.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  Divider,
  Chip,
  useTheme,
  alpha,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Campaign as CampaignIcon,
  AttachMoney as BudgetIcon,
  Flag as GoalIcon,
  Public as ReachIcon,
  ThumbUp as EngagementIcon,
  Timeline as ConversionIcon,
  ArrowBack as BackIcon,
  NavigateNext as NavigateNextIcon,
  FileDownload as DownloadIcon,
  Public,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
} from 'recharts';

// Import custom components
import MagicLoading from '../../components/ui/MagicLoading';
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import MetricComparison from '../../components/features/analytics/MetricComparison';

// Import store
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';
import useCampaignStore from '../../stores/campaignStore';

/**
 * Campaign Analytics Page Component
 */
const CampaignAnalytics = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [selectedCampaign, setSelectedCampaign] = useState(id || 'all');
  
  const { 
    campaignAnalytics, 
    isLoading, 
    error, 
    fetchCampaignAnalytics,
    dateRange,
    setDateRange,
    exportAnalyticsData,
  } = useAnalyticsStore();
  
  const {
    campaigns,
    fetchCampaigns,
    isLoading: campaignsLoading,
  } = useCampaignStore();
  
  const { showError } = useNotificationStore();
  
  // Fetch campaigns and analytics data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!campaigns || campaigns.length === 0) {
          await fetchCampaigns();
        }
        
        await fetchCampaignAnalytics(selectedCampaign !== 'all' ? selectedCampaign : null);
      } catch (error) {
        showError('Failed to load campaign analytics');
      }
    };
    
    loadData();
  }, [fetchCampaignAnalytics, fetchCampaigns, showError, selectedCampaign, dateRange]);
  
  // Handle campaign selection change
  const handleCampaignChange = (event) => {
    const campaignId = event.target.value;
    setSelectedCampaign(campaignId);
    
    // Update URL if a specific campaign is selected
    if (campaignId !== 'all') {
      navigate(`/analytics/campaigns/${campaignId}`);
    } else {
      navigate('/analytics/campaigns');
    }
  };
  
  // Handle date range change
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange(startDate, endDate);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async () => {
    try {
      const { startDate, endDate } = dateRange;
      const data = await exportAnalyticsData({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: 'excel',
        campaignId: selectedCampaign !== 'all' ? selectedCampaign : null,
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign-analytics-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showError('Failed to export analytics data');
    }
  };
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  
  // Check if data is available
  const hasData = !isLoading && campaignAnalytics;
  
  // Loading state
  if ((isLoading && !campaignAnalytics) || campaignsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoading variant="mystical" size="large" message="Loading campaign analytics..." />
      </Box>
    );
  }
  
  // Get campaign name for the header
  const getCampaignName = () => {
    if (selectedCampaign === 'all') return 'All Campaigns';
    
    const campaign = campaigns?.find(c => c._id === selectedCampaign);
    return campaign ? campaign.name : 'Campaign';
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link 
            color="inherit" 
            onClick={() => navigate('/analytics')}
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Campaign Analytics</Typography>
          {selectedCampaign !== 'all' && (
            <Typography color="text.primary">{getCampaignName()}</Typography>
          )}
        </Breadcrumbs>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mr: 2 }}>
              {selectedCampaign === 'all' ? 'Campaign Analytics' : 'Campaign Performance'}
            </Typography>
            
            {/* Campaign selector */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="campaign-select-label">Campaign</InputLabel>
              <Select
                labelId="campaign-select-label"
                id="campaign-select"
                value={selectedCampaign}
                label="Campaign"
                onChange={handleCampaignChange}
              >
                <MenuItem value="all">All Campaigns</MenuItem>
                {campaigns?.map((campaign) => (
                  <MenuItem key={campaign._id} value={campaign._id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleDateRangeChange}
              size="small"
            />
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportAnalytics}
            >
              Export Report
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* Error message */}
      {error && (
        <Card sx={{ p: 2, mb: 4, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}
      
      {/* Overview KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Budget"
            value={hasData ? campaignAnalytics.budget?.total : null}
            unit={hasData ? campaignAnalytics.budget?.currency : '$'}
            icon={<BudgetIcon />}
            isLoading={isLoading}
            magical
            formatValue={formatCurrency}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Budget Spent"
            value={hasData ? campaignAnalytics.budget?.spent : null}
            unit={hasData ? campaignAnalytics.budget?.currency : '$'}
            percentChange={hasData ? campaignAnalytics.budget?.spentPercentChange : null}
            icon={<BudgetIcon />}
            color="secondary"
            isLoading={isLoading}
            magical
            formatValue={formatCurrency}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Reach"
            value={hasData ? campaignAnalytics.reach?.total : null}
            previousValue={hasData ? campaignAnalytics.reach?.previousTotal : null}
            percentChange={hasData ? campaignAnalytics.reach?.percentChange : null}
            icon={<ReachIcon />}
            color="info"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Engagement Rate"
            value={hasData ? campaignAnalytics.engagement?.rate : null}
            unit="%"
            previousValue={hasData ? campaignAnalytics.engagement?.previousRate : null}
            percentChange={hasData ? campaignAnalytics.engagement?.percentChange : null}
            icon={<EngagementIcon />}
            color="success"
            isLoading={isLoading}
            magical
          />
        </Grid>
      </Grid>
      
      {/* Campaign Performance Charts */}
      <Grid container spacing={3}>
        {/* Performance Over Time Chart */}
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Campaign Performance Over Time"
            subtitle="Key metrics progression"
            icon={<TrendingUpIcon />}
            isLoading={isLoading}
            height={400}
            magical
          >
            {hasData && campaignAnalytics.timeSeriesData && (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={campaignAnalytics.timeSeriesData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="reach"
                    name="Reach"
                    stroke={theme.palette.primary.main}
                    activeDot={{ r: 8 }}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="engagement"
                    name="Engagement"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="clicks"
                    name="Clicks"
                    stroke={theme.palette.info.main}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="conversions"
                    name="Conversions"
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        
        {/* Budget Allocation */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard
            title="Budget Allocation"
            subtitle="Distribution by platform"
            icon={<BudgetIcon />}
            isLoading={isLoading}
            height={400}
            color="secondary"
            magical
          >
            {hasData && campaignAnalytics.budget?.allocation && (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={Object.entries(campaignAnalytics.budget.allocation)
                      .filter(([_, value]) => value > 0)
                      .map(([platform, value]) => ({
                        platform: platform.charAt(0).toUpperCase() + platform.slice(1),
                        value,
                      }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="platform"
                    label={(entry) => `${entry.platform}: ${entry.value}%`}
                    labelLine={false}
                  >
                    {Object.entries(campaignAnalytics.budget.allocation)
                      .filter(([_, value]) => value > 0)
                      .map((_, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={chartColors[index % chartColors.length]} 
                        />
                      ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name, props) => [`${value}%`, props.payload.platform]}
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        
        {/* Goal Completion */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard
            title="KPI Performance"
            subtitle="Goal completion by KPI"
            icon={<GoalIcon />}
            isLoading={isLoading}
            height={400}
            color="info"
          >
            {hasData && campaignAnalytics.kpis && (
              <Box sx={{ height: 350, overflowY: 'auto', p: 1 }}>
                {campaignAnalytics.kpis.map((kpi, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ flexGrow: 1 }}>
                        {kpi.name}
                      </Typography>
                      <Typography variant="body2">
                        {kpi.completionRate}%
                      </Typography>
                    </Box>
                    <MetricComparison
                      title={kpi.category}
                      currentValue={kpi.current}
                      targetValue={kpi.target}
                      unit={kpi.unit}
                      tooltip={`Target: ${kpi.target} ${kpi.unit || ''}`}
                      color="info"
                    />
                  </Box>
                ))}
              </Box>
            )}
          </ChartCard>
        </Grid>
        
        {/* Platform Performance */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard
            title="Platform Performance"
            subtitle="Comparison by platform"
            icon={<Public />}
            isLoading={isLoading}
            height={400}
          >
            {hasData && campaignAnalytics.platformPerformance && (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={campaignAnalytics.platformPerformance}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    type="number"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    dataKey="platform"
                    type="category"
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Legend />
                  <Bar 
                    dataKey="reach" 
                    name="Reach" 
                    fill={theme.palette.primary.main} 
                    barSize={20}
                  />
                  <Bar 
                    dataKey="engagement" 
                    name="Engagement" 
                    fill={theme.palette.secondary.main} 
                    barSize={20}
                  />
                  <Bar 
                    dataKey="conversions" 
                    name="Conversions" 
                    fill={theme.palette.success.main} 
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        
        {/* Conversion Funnel */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard
            title="Conversion Funnel"
            subtitle="Campaign user journey"
            icon={<ConversionIcon />}
            isLoading={isLoading}
            height={400}
            color="success"
            magical
          >
            {hasData && campaignAnalytics.conversionFunnel && (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart
                  data={campaignAnalytics.conversionFunnel}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis 
                    dataKey="stage" 
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <YAxis 
                    tick={{ fill: theme.palette.text.secondary }}
                    stroke={theme.palette.divider}
                  />
                  <RechartsTooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    fill={alpha(theme.palette.success.main, 0.2)} 
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        
        {selectedCampaign !== 'all' && (
          <Grid item xs={12}>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<CampaignIcon />}
                onClick={() => navigate(`/campaigns/${selectedCampaign}`)}
              >
                View Campaign Details
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CampaignAnalytics;